import { Button, InputNumber, message, Modal, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PrintJob } from '../types';
import { MAX_COPIES_PER_CODE } from '../utils/sortCodeUtils';
import { BagStatusTag } from './BatchTags';
import { PrintQuantityModalProps } from './overlayProps';

/** 份数输入值：null 表示输入框被清空 */
type Copies = number | null;

/** 份数范围 1~MAX_COPIES_PER_CODE，与批量建包「每码包牌数」同一上限，防止误输入导致打印份数失控 */
const clampCopies = (n: number): number => Math.min(MAX_COPIES_PER_CODE, Math.max(1, n));

const initialCopiesOf = (jobs: PrintJob[]): Copies[] => jobs.map((j) => clampCopies(Math.floor(Number(j.copies) || 1)));

interface PrintGroup {
    batchNumber: string;
    /** 该批次的任务在 jobs 中的下标 */
    indexes: number[];
}

/**
 * 打印份数弹窗（原型 printQty / openPrintQty / setCopies / confirmPrintQty）：每个包牌可单独设置份数（≥1），
 * 也可统一设置；任务跨多个批次时按批次分组，组头可「本批次应用统一份数」。
 */
export const PrintQuantityModal: React.FC<PrintQuantityModalProps> = ({ open, jobs, onClose, onConfirm }) => {
    const { t } = useTranslation();
    const [copies, setCopies] = useState<Copies[]>(() => initialCopiesOf(jobs));
    const [uniform, setUniform] = useState<Copies>(1);

    // 每次打开（或任务变化）时按 jobs 重置可编辑份数
    useEffect(() => {
        if (!open) return;
        setCopies(initialCopiesOf(jobs));
        setUniform(1);
    }, [open, jobs]);

    const groups = useMemo<PrintGroup[]>(() => {
        const order: PrintGroup[] = [];
        const byBatch = new Map<string, PrintGroup>();
        jobs.forEach((j, i) => {
            const key = j.batchNumber || '—';
            let group = byBatch.get(key);
            if (!group) {
                group = { batchNumber: key, indexes: [] };
                byBatch.set(key, group);
                order.push(group);
            }
            group.indexes.push(i);
        });
        return order;
    }, [jobs]);

    const multi = groups.length > 1;
    const countAt = (i: number): number => copies[i] || 0;
    const sumOf = (indexes: number[]): number => indexes.reduce((a, i) => a + countAt(i), 0);
    const total = jobs.reduce((a, _, i) => a + countAt(i), 0);

    const setAt = (indexes: number[], value: Copies) => {
        setCopies((prev) => {
            const next = [...prev];
            indexes.forEach((i) => { next[i] = value; });
            return next;
        });
    };
    const applyUniform = (indexes: number[]) => setAt(indexes, clampCopies(uniform || 1));

    const handleConfirm = () => {
        if (!total) {
            message.warning(t('wms.sortingBatch.print.copiesAtLeastOne'));
            return;
        }
        // 清空的输入框按 1 份处理（与原型预览一致）
        onConfirm(jobs.map((j, i) => ({ ...j, copies: clampCopies(copies[i] || 1) })));
    };

    const renderRow = (job: PrintJob, i: number, n: number) => (
        <div
            key={`${job.bagNumber}-${i}`}
            className={`flex items-center border-0 border-b border-solid border-[#f2f3f5] ${n % 2 ? 'bg-[#fcfcfd]' : 'bg-white'}`}
        >
            <span className="w-[150px] flex-none py-2 pl-4 font-mono text-[12.5px] text-[#1d2129]">{job.bagNumber}</span>
            <span className="w-[140px] flex-none truncate py-2 pl-3 font-mono text-[12.5px] text-[#4e5969]">{job.sortingCode}</span>
            <span className="min-w-0 flex-1 truncate py-2 pl-3 font-mono text-xs text-[#86909c]">
                {multi ? <BagStatusTag status={job.status} /> : job.batchNumber}
            </span>
            <span className="flex w-[128px] flex-none justify-end py-1.5 pr-4">
                <Space.Compact size="small">
                    <Button
                        aria-label={t('wms.sortingBatch.print.decrease') || undefined}
                        onClick={() => setAt([i], clampCopies((copies[i] || 1) - 1))}
                    >
                        −
                    </Button>
                    <InputNumber
                        aria-label={`${t('wms.sortingBatch.print.columns.copies')} ${job.bagNumber}`}
                        className="w-12"
                        controls={false}
                        min={1}
                        max={MAX_COPIES_PER_CODE}
                        precision={0}
                        value={copies[i] ?? null}
                        onChange={(v) => setAt([i], typeof v === 'number' ? clampCopies(v) : v)}
                    />
                    <Button
                        aria-label={t('wms.sortingBatch.print.increase') || undefined}
                        onClick={() => setAt([i], clampCopies((copies[i] || 1) + 1))}
                    >
                        +
                    </Button>
                </Space.Compact>
            </span>
        </div>
    );

    return (
        <Modal
            open={open}
            width={840}
            destroyOnHidden
            title={t('wms.sortingBatch.print.title')}
            okText={t('wms.sortingBatch.print.confirm')}
            cancelText={t('common.cancel')}
            onOk={handleConfirm}
            onCancel={onClose}
        >
            <div className="mb-3 flex flex-wrap items-center gap-2.5">
                <span className="text-[12.5px] text-[#86909c]">
                    {t('wms.sortingBatch.print.summary', { bags: jobs.length, labels: total })}
                </span>
                <div className="min-w-[8px] flex-1" />
                <span className="whitespace-nowrap text-[12.5px] text-[#4e5969]">{t('wms.sortingBatch.print.uniformCopies')}</span>
                <InputNumber
                    aria-label={t('wms.sortingBatch.print.uniformCopies') || undefined}
                    size="small"
                    className="w-14"
                    min={1}
                    max={MAX_COPIES_PER_CODE}
                    precision={0}
                    value={uniform}
                    onChange={(v) => setUniform(typeof v === 'number' ? clampCopies(v) : v)}
                />
                <Button size="small" onClick={() => applyUniform(jobs.map((_, i) => i))}>
                    {t('wms.sortingBatch.print.applyAll')}
                </Button>
            </div>
            <div className="overflow-hidden rounded-sm border border-solid border-[#e5e6eb]">
                <div className="flex items-center border-0 border-b border-solid border-[#e5e6eb] bg-[#f7f8fa] py-2 text-xs font-medium text-[#4e5969]">
                    <span className="w-[150px] flex-none pl-4">{t('wms.sortingBatch.print.columns.bagNumber')}</span>
                    <span className="w-[140px] flex-none pl-3">{t('wms.sortingBatch.print.columns.sortingCode')}</span>
                    <span className="min-w-0 flex-1 pl-3">
                        {t(multi ? 'wms.sortingBatch.print.columns.bagStatus' : 'wms.sortingBatch.print.columns.batch')}
                    </span>
                    <span className="w-[128px] flex-none pr-4 text-center">{t('wms.sortingBatch.print.columns.copies')}</span>
                </div>
                <div className="max-h-[min(56vh,520px)] overflow-auto">
                    {groups.map((g) => (
                        <div key={g.batchNumber}>
                            {multi && (
                                <div className="sticky top-0 z-[1] flex items-center gap-2.5 border-0 border-b border-solid border-[#e9dcff] bg-[#faf7ff] px-4 py-2">
                                    <span className="truncate font-mono text-[12.5px] font-medium text-[#1d2129]">{g.batchNumber}</span>
                                    <span className="whitespace-nowrap text-xs text-[#86909c]">
                                        {t('wms.sortingBatch.print.groupSummary', { bags: g.indexes.length, labels: sumOf(g.indexes) })}
                                    </span>
                                    <div className="flex-1" />
                                    <Button size="small" type="primary" ghost onClick={() => applyUniform(g.indexes)}>
                                        {t('wms.sortingBatch.print.applyGroup')}
                                    </Button>
                                </div>
                            )}
                            {g.indexes.map((i, n) => renderRow(jobs[i], i, n))}
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};
